import { baseApi as api } from "../../../contexts/endpoints/base_api";
import { Address } from "../../../contexts/endpoints/shared/types";

const injectedApi = api.injectEndpoints({
  endpoints: (build) => ({
    getTraining: build.query<GetTrainingApiResponse, GetTrainingApiArg>({
      query: () => ({ url: `/api/account/training` }),
    }),
    getTestPricing: build.query<
      GetTestPricingApiResponse,
      GetTestPricingApiArg
    >({
      query: (queryArg) => ({
        url: `/api/account/training/test/pricing/${queryArg.level}`,
      }),
    }),
    postTestPurchase: build.mutation<
      PostTestPurchaseApiResponse,
      PostTestPurchaseApiArg
    >({
      query: (queryArg) => ({
        url: `/api/account/training/test/purchase`,
        method: "POST",
        body: queryArg.purchaseTestPayload,
      }),
    }),
    postCalculateTax: build.mutation<
      PostCalculateTaxApiResponse,
      PostCalculateTaxApiArg
    >({
      query: (queryArg) => ({
        url: `/api/account/training/test/calculateTax`,
        method: "POST",
        body: queryArg.calculateCertTaxPayload,
      }),
    }),
    postRedeemTestCredit: build.mutation<
      PostRedeemTestCreditApiResponse,
      PostRedeemTestCreditApiArg
    >({
      query: (queryArg) => ({
        url: `/api/account/training/test/redeemCredit`,
        method: "POST",
        body: queryArg.redeemTestCreditPayload,
      }),
    }),
    postUploadFiles: build.mutation<
      PostUploadFilesApiResponse,
      PostUploadFilesApiArg
    >({
      query: (queryArg) => ({
        url: `/api/account/uploadFiles`,
        method: "POST",
        body: queryArg.body,
        params: {
          name: queryArg.name,
        },
      }),
    }),
    deleteFile: build.mutation<DeleteFileApiResponse, DeleteFileApiArg>({
      query: (queryArg) => ({
        url: `/api/account/deleteFile/${queryArg.fileId}`,
        method: "DELETE",
      }),
    }),
  }),
  overrideExisting: false,
});

export type GetTrainingApiResponse = /** status 200 OK */ TrainingDetails;
export type GetTrainingApiArg = void;
export type GetTestPricingApiResponse = /** status 200 OK */ TestPricing;
export interface GetTestPricingApiArg {
  level: string;
}
export type PostTestPurchaseApiResponse =
  /** status 200 OK */ CertificationTestReceipt;
export interface PostTestPurchaseApiArg {
  purchaseTestPayload: PurchaseTestPayload;
}
export type PostCalculateTaxApiResponse =
  /** status 200 OK */ CrmCertTaxResponse;
export interface PostCalculateTaxApiArg {
  calculateCertTaxPayload: CalculateCertTaxPayload;
}
export type PostRedeemTestCreditApiResponse =
  /** status 200 OK */ CertificationTestCreditRedemption;
export interface PostRedeemTestCreditApiArg {
  redeemTestCreditPayload: RedeemTestCreditPayload;
}
export type PostUploadFilesApiResponse = /** status 200 OK */ object;
export interface PostUploadFilesApiArg {
  name: string;
  body: {
    doc: Blob;
  };
}
export type DeleteFileApiResponse = /** status 200 OK */ object;
export interface DeleteFileApiArg {
  fileId: string;
}

export interface CertificationHistory {
  version?: string;
  iconUrl?: string;
  earnDate?: string;
  earnMethod?: string;
}
export interface Certification {
  status: string;
  version?: string;
  earnDate?: string;
  expirationDate?: string;
  earnMethod?: string;
  iconUrl?: string;
  testDeadline?: string;
  testUrl?: string;
  history?: CertificationHistory[];
}
export interface Topic {
  name: string;
  completed: boolean;
}
export interface Course {
  name: string;
  iconUrl: string;
  progress: number;
  topics: Topic[];
}
export interface Credential {
  version: string;
  iconUrl: string;
  dateEarned?: string;
  status: "NOT_STARTED" | "IN_PROGRESS" | "EARNED";
  courses: Course[];
}
export interface VersionedCredentialHistory {
  version: string;
  iconUrl: string;
  dateEarned: string;
}
export interface EmployeeCourse {
  name: string;
  iconUrl: string;
  status: "IN_PROGRESS" | "COMPLETED";
}
export interface TrainingDetails {
  certifications?: Record<string, Certification>;
  credential?: Credential;
  credentialHistory?: VersionedCredentialHistory[];
  employeeCourses?: EmployeeCourse[];
}
export interface TestCredit {
  id: number;
  name?: string;
  detail?: string;
  remaining?: number;
  type?: string;
}
export interface TestPricing {
  testCertLevel: string;
  discountedPrice: number;
  price: number;
  name: string;
  discount: number;
  description: string;
  active: boolean;
  practiceTestURL: string;
  rateID: number;
  canPurchaseOnline: boolean;
  creditsAvailable?: TestCredit[];
  inCooldownPeriod: boolean;
}
export interface CertificationTestSummary {
  startDate?: string;
  endDate?: string;
  testID?: number;
  contactID?: number;
}
export interface CertificationTestCreditRedemption {
  success?: boolean;
  status?: string;
  message?: string;
  company?: string;
  testCreated?: boolean;
  grandTotalDiscount?: number;
  creditRedeemed?: boolean;
  certTests?: CertificationTestSummary[];
}
export interface RedeemTestCreditPayload {
  rateId: number;
  creditId: number;
}
export interface CertificationTestReceipt {
  success?: boolean;
  cardCharged?: boolean;
  cardError?: boolean;
  orderNumber?: number;
  firstName?: string;
  lastName?: string;
  total?: number;
  retailTotal?: number;
  salesTax?: number;
  ccLast4?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
}
export interface PurchaseTestPayload {
  nameOnCard?: string;
  ccToken?: string;
  rateId: number;
  salesTax: number;
  total: number;
  address: Address;
}
export interface ValidatedBillingAddress {
  billingAddress1: string;
  billingCountry: string;
  billingState: string;
  billingCity: string;
  billingZip: string;
}
export interface CrmCertTaxResponse {
  validatedAddress?: ValidatedBillingAddress;
  salesTaxTotal?: number;
  status: "SUCCESS" | "ERROR" | "FAILURE";
}
export interface CalculateCertTaxPayload {
  billingEmail: string;
  billingAddress1: string;
  billingAddress2: string;
  billingCity: string;
  billingState: string;
  billingCountry: string;
  billingZip: string;
  total: number;
  rateId: number;
}

export const {
  useGetTrainingQuery,
  useGetTestPricingQuery,
  usePostTestPurchaseMutation,
  usePostCalculateTaxMutation,
  usePostRedeemTestCreditMutation,
  usePostUploadFilesMutation,
  useDeleteFileMutation,
} = injectedApi;
